import { ApiError, api } from "./api";
import { authStorage } from "./auth-storage";

const apiBase = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "")
  ?? "http://localhost:5080/api";
const apiOrigin = apiBase.replace(/\/api$/, "");

export const ProviderDocumentType = { Ine: 0, ProofOfAddress: 1, Curp: 2, ActaConstitutiva: 3 } as const;
export type ProviderDocumentType = typeof ProviderDocumentType[keyof typeof ProviderDocumentType];

export const ProviderDocumentStatus = { Pending: 0, Verified: 1, Rejected: 2 } as const;
export type ProviderDocumentStatus = typeof ProviderDocumentStatus[keyof typeof ProviderDocumentStatus];

export type ProviderDocument = {
  id: string;
  type: ProviderDocumentType;
  fileUrl: string;
  fileName: string;
  status: ProviderDocumentStatus;
  reviewNote: string | null;
  uploadedAt: string;
  reviewedAt: string | null;
};

function absolute(url: string): string {
  return url.startsWith("http") ? url : `${apiOrigin}${url}`;
}

export async function listProviderDocuments(): Promise<ProviderDocument[]> {
  const docs = await api<ProviderDocument[]>("/provider/documents");
  return docs.map((d) => ({ ...d, fileUrl: absolute(d.fileUrl) }));
}

export async function uploadProviderDocument(type: ProviderDocumentType, file: File): Promise<ProviderDocument> {
  const form = new FormData();
  form.append("type", String(type));
  form.append("file", file);

  const tokens = authStorage.get();
  const headers: Record<string, string> = {};
  if (tokens?.accessToken) headers["Authorization"] = `Bearer ${tokens.accessToken}`;
  // Do NOT set Content-Type — the browser adds the multipart boundary.

  const res = await fetch(`${apiBase}/provider/documents`, { method: "POST", headers, body: form });
  const text = await res.text();
  let parsed: unknown = null;
  try { parsed = text ? JSON.parse(text) : null; } catch { parsed = text; }

  if (!res.ok) {
    const msg = typeof parsed === "object" && parsed && "error" in parsed
      ? String((parsed as { error: unknown }).error)
      : `Upload failed (HTTP ${res.status})`;
    throw new ApiError(res.status, msg, parsed);
  }

  const out = parsed as ProviderDocument;
  return { ...out, fileUrl: absolute(out.fileUrl) };
}

export function deleteProviderDocument(id: string): Promise<void> {
  return api<void>(`/provider/documents/${id}`, { method: "DELETE" });
}
